import Compressor from 'compressorjs';
import CoreAppService from '../_shared/api/coreAppService';
import Singleton from '../Singleton';
import TransactionRouterService from '../_shared/api/transactionRouterService';
import {
  PaymentOnHoldDocumentType,
  UploadAttachmentResponse
} from '../global.d';

export default class PaymentsOnHoldUploadService extends Singleton<
  PaymentsOnHoldUploadService
> {
  /**
   * Public Methods
   */
  static generatePresignedUrls = async (
    tradeId: number,
    documentType: number,
    files: Blob[]
  ) => {
    const fileTypes: string[] = [];
    for (let i = 0; i < files.length; i += 1) {
      if (files[i].size > maxFileSizeInBytes) {
        return null;
      }
      if (documentType === titleDocumentType && files[i].type !== pdfFileType) {
        return null;
      }
      fileTypes.push(files[i].type);
    }
    const client = await CoreAppService();
    try {
      const presignedUploadUrls = await client.paymentsOnHold.generatePresignedUrl(
        {
          tradeId,
          documentType,
          fileTypes
        }
      );
      return presignedUploadUrls?.data;
    } catch (e) {
      return e?.data;
    }
  };

  static generateEtrPresignedUrl = async (
    purchaseId: string,
    documentType: PaymentOnHoldDocumentType,
    file: File
  ): Promise<UploadAttachmentResponse> => {
    if (file.size > maxFileSizeInBytes) {
      throw new Error('Invalid file size');
    }

    if (documentType === 'Title' && file.type !== pdfFileType) {
      throw new Error('Invalid file type');
    }

    const client = await TransactionRouterService();
    try {
      const presignedUploadUrl = await client.api.postPaymentOnHoldPurchaseIdDocumentUploadTokens(
        purchaseId,
        {
          fileName: file.name,
          contentType: file.type,
          contentLength: file.size
        }
      );

      return presignedUploadUrl?.data;
    } catch (e) {
      throw new Error('Failed to generate URL');
    }
  };

  static markDocumentUploadStatus = async (
    documentIds: number[],
    isUploaded: boolean
  ) => {
    const client = await CoreAppService();
    try {
      const updateStatusResponse = await client.paymentsOnHold.updatePaymentOnHoldDocumentStatus(
        {
          documentIds,
          isUploaded
        }
      );
      return updateStatusResponse?.data;
    } catch (e) {
      return e?.data;
    }
  };

  static uploadFileToS3UsingPresignedUrl = async (
    presignedUrl: string,
    file: Blob
  ) => {
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-amz-acl': 'bucket-owner-full-control'
      },
      body: file
    };
    const uploadToS3Response = await fetch(presignedUrl, requestOptions);
    if (uploadToS3Response.status !== 200) {
      throw new Error('Upload to S3 Error');
    }
  };

  static uploadFile = (presignedUrl: string, file: Blob) => {
    return new Promise(compressAndUploadFileToS3);

    async function compressAndUploadFileToS3(resolve: any, reject: any) {
      // only compress image files
      if (compressableFileTypes.indexOf(file.type) !== -1) {
        (() =>
          new Compressor(file, {
            quality: 0.8,
            convertSize: Infinity,
            success: async compressedFile => {
              await PaymentsOnHoldUploadService.uploadFileToS3UsingPresignedUrl(
                presignedUrl,
                compressedFile
              ).catch(() => {
                reject();
              });
              resolve(presignedUrl);
            }
          }))();
      } else {
        await PaymentsOnHoldUploadService.uploadFileToS3UsingPresignedUrl(
          presignedUrl,
          file
        ).catch(() => {
          reject();
        });
        resolve(presignedUrl);
      }
    }
  };
}

export const compressableFileTypes = ['image/jpeg', 'image/png'];
export const maxFileSizeInBytes = 10000000; // 10MB
export const pdfFileType = 'application/pdf';
export const titleDocumentType = 0;
